import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Colors from "../../../../Helper/ColorsConstant";
import classes from "./WidgetCardsLandscape.module.css";

const getHeaderColor = (percent) => {
    const pct = parseFloat(String(percent || "0").replace('%', '')) || 0;
    if (pct >= 100) return "#1a1a1a";
    if (pct < 100 && pct >= 66.66) return "rgb(186, 77, 81)"//"#de0518";//"rgb(229, 82, 83)";  //red
    if (pct < 66.66 && pct >= 33.33) return "rgb(232, 194, 103)"; //yellow
    if (pct < 33.33) return "rgb(154, 181, 126)";//"rgb(154, 181, 126)"; //green
    return "#1a1a1a";
};

const getStackHeaderBorderColor = (headerColor) => {
    const match = headerColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (!match) return "rgba(255,255,255,0.2)";
    const factor = 0.68;
    return `rgb(${Math.round(parseInt(match[1]) * factor)}, ${Math.round(parseInt(match[2]) * factor)}, ${Math.round(parseInt(match[3]) * factor)})`;
};

const areSameCards = (a, b) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        const p = a[i]; const n = b[i];
        if (
            p.id !== n.id || p.title !== n.title || p.description !== n.description ||
            p.owner !== n.owner || p.percent !== n.percent || p.topLeftText !== n.topLeftText ||
            p.imageUrl !== n.imageUrl || p.openDate !== n.openDate || p.promiseDate !== n.promiseDate ||
            p.status !== n.status || p.headerText !== n.headerText ||
            p.completedDate !== n.completedDate || p.targetHours !== n.targetHours ||
            p.actualHours !== n.actualHours || p.description1 !== n.description1
        ) return false;
    }
    return true;
};


export default function WidgetCardsLandscape({
    cardsData = [],
    minOpenTicketCount,
    autoRevertDurationinSec = 0,
    isDarkTheme = false,
    OpenTicketPlacement,
    widgetViewportWidth = 0,
    widgetViewportHeight = 0,
}) {
    const parentRef = useRef(null);
    const gridRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const cardRefs = useRef([]);
    const autoRevertTimerRef = useRef(null);
    const resizeDebounceRef = useRef(null);
    const lastCalcTimeRef = useRef(0);
    const initialStateRef = useRef(null);
    const isFirstCalculationRef = useRef(true);
    const calcRetryRef = useRef(0);
    const calcHandlerRef = useRef(null);
    const calcRunIdRef = useRef(0);
    const dataVersionRef = useRef(0);  // Track data version to prevent stale state restoration
    const isCalculatingRef = useRef(false);  // Track if calculation is in progress
    const hasUserInteractedRef = useRef(false);
    const prevDisplayCardsRef = useRef([]);
    const prevLayoutDepsRef = useRef({ minOpenTicketCount: null, isOpenTicketPlacementTop: null });
    const isEntryAnimatingRef = useRef(false);   // true while initial load animation is playing
    const [entryAnimStage, setEntryAnimStage] = useState(0); // 0=idle, 1=stack, 2=headers, 3=bodies
    const entryAnimTimersRef = useRef([]);
    const [scrollEnabled, setScrollEnabled] = useState(false);
    const prevExpandedRef = useRef(new Set());
    const layoutMeasureTokenRef = useRef(0);
    const appliedMeasureTokenRef = useRef(-1);
    const colors = Colors;
    const normalizedCardsData = useMemo(() => (Array.isArray(cardsData) ? cardsData : []), [cardsData]);

    const defaultWidthOfOpenCard = 220 //290;
    const defaultOpenHeaderBlockPx = 26;
    const MIN_MEDIUM_HEADERS = 3;

    const isOpenTicketPlacementTop = useMemo(
        () => String(OpenTicketPlacement || "bottom").toLowerCase() === "top",
        [OpenTicketPlacement]
    );
    const safeMinOpenCountForRender = useMemo(
        () => (Number.isFinite(Number(minOpenTicketCount)) ? Math.max(1, Math.min(5, Math.floor(Number(minOpenTicketCount)))) : 1),
        [minOpenTicketCount]
    );
    const estimatedTicketsToRender = useMemo(() => {
        const totalTickets = normalizedCardsData.length;
        if (totalTickets === 0) return 0;

        const minOpen = Math.min(safeMinOpenCountForRender, totalTickets);
        const containerPadPx = 2;
        const effectiveWidth = Math.max(0, widgetViewportWidth - (2 * containerPadPx));
        if (effectiveWidth <= 0) return minOpen;

        const estimatedCardSpacingPx = 2.5; // Average spacing based on measurements, can be refined further
        const estimatedOpenHeaderBlockPx = defaultOpenHeaderBlockPx;

        const estimatedOpenBodyPerTicketPx = defaultWidthOfOpenCard;

        const estimatedHighlyCollapsedHeaderBlockPx = 4;
        const estimatedMediumHeaderBlockPx = defaultOpenHeaderBlockPx;
        const perCollapsedTicketPx = estimatedHighlyCollapsedHeaderBlockPx + estimatedCardSpacingPx;
        const perMediumCollapsedTicketPx = estimatedMediumHeaderBlockPx + estimatedCardSpacingPx;


        let fittedOpenCount = minOpen;
        while (fittedOpenCount > 0) {
            const openTicketsPx = fittedOpenCount * (estimatedOpenHeaderBlockPx + estimatedOpenBodyPerTicketPx);
            const openSpacingPx = Math.max(0, fittedOpenCount - 1) * estimatedCardSpacingPx;
            if ((openTicketsPx + openSpacingPx) <= effectiveWidth) {
                break;
            }
            fittedOpenCount -= 1;
        }

        const reservedOpenTicketsPx = fittedOpenCount * (estimatedOpenHeaderBlockPx + estimatedOpenBodyPerTicketPx);
        const reservedOpenSpacingPx = Math.max(0, fittedOpenCount - 1) * estimatedCardSpacingPx;
        let remainingHeightPx = Math.max(0, effectiveWidth - (reservedOpenTicketsPx + reservedOpenSpacingPx));

        const baseTickets = fittedOpenCount;

        // Guarantee at least MIN_MEDIUM_HEADERS medium (orphan) headers among non-open slots.
        const nonOpenTickets = totalTickets - fittedOpenCount;
        const guaranteedMediumCount = Math.min(nonOpenTickets, MIN_MEDIUM_HEADERS);
        const guaranteedMediumPx = guaranteedMediumCount * perMediumCollapsedTicketPx;

        // Budget remaining after reserving space for guaranteed medium headers.
        const remainingAfterGuaranteedMedium = Math.max(0, remainingHeightPx - guaranteedMediumPx);

        // Any leftover width → extra medium + collapsed tickets.
        const mixWeightMedium = 0.33;
        const mediumHeightBudget = remainingAfterGuaranteedMedium * mixWeightMedium;
        const collapsedHeightBudget = Math.max(0, remainingAfterGuaranteedMedium - mediumHeightBudget);
        const extraMediumCollapsedTickets = Math.max(0, Math.floor(mediumHeightBudget / perMediumCollapsedTicketPx));
        const additionalCollapsedTickets = Math.max(0, Math.floor(collapsedHeightBudget / perCollapsedTicketPx));

        return Math.max(
            baseTickets,
            Math.min(totalTickets, baseTickets + guaranteedMediumCount + extraMediumCollapsedTickets + additionalCollapsedTickets)
        );
    }, [normalizedCardsData.length, safeMinOpenCountForRender, widgetViewportWidth]);

    console.log("Estimated tickets to render based on viewport width:", estimatedTicketsToRender, "for viewport width:", widgetViewportWidth);

    const [maxTicketsToRender, setMaxTicketsToRender] = useState(estimatedTicketsToRender);

    useEffect(() => {
        setMaxTicketsToRender((prev) => {
            const boundedEstimate = Math.max(
                safeMinOpenCountForRender,
                Math.min(normalizedCardsData.length, estimatedTicketsToRender)
            );
            console.log("Adjusting maxTicketsToRender from", prev, "to", boundedEstimate, "based on new estimate:", estimatedTicketsToRender);
            if (boundedEstimate === prev) return prev;
            console.log("Updated maxTicketsToRender to:", boundedEstimate);
            return boundedEstimate;
        });
    }, [estimatedTicketsToRender, normalizedCardsData.length, safeMinOpenCountForRender]);

    const displayCardsData = useMemo(
        () => {
            console.log("Rendering with maxTicketsToRender:", maxTicketsToRender, "out of", normalizedCardsData.length, normalizedCardsData.slice(-maxTicketsToRender));
            return normalizedCardsData.slice(-maxTicketsToRender)
        },
        [normalizedCardsData, maxTicketsToRender]
    );
    const themeStyles = useMemo(() => ({
        cardBackground: isDarkTheme ? "rgb(52, 52, 52)" : "#ffffff",
        cardBorder: isDarkTheme ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.12)",
        cardBoxShadow: isDarkTheme ? "0 2px 8px rgba(0,0,0,0.60)" : "none",
        bodyTextColor: isDarkTheme ? "#d0d0d0" : "inherit",
        mutedTextColor: isDarkTheme ? "#a8a8a8" : "#6c757d",
    }), [isDarkTheme]);

    const [cardStates, setCardStates] = useState(
        displayCardsData.map((_, index) => ({
            isExpanded: index === 0,
            isHalfOpen: false,
            fullHeight: 0,  // Store full height for accurate half-open calculation
            visibleBodyHeight: 0,
            renderMode: index === 0 ? "open" : "collapsed",
            priorCompressedMode: "collapsed",
        }))
    );
    const [isReady, setIsReady] = useState(false);  // Hide cards until first calc completes
    const [disableBodyTransition, setDisableBodyTransition] = useState(true);
    const [disableHeaderTransition, setDisableHeaderTransition] = useState(true);
    const isRevertAnimatingRef = useRef(false);
    const revertAnimationTimerRef = useRef(null);
    const hasInitiallyLoadedRef = useRef(false);  // Track if we've loaded at least once
    const isMeasuringRef = useRef(false);  // Track if we're in the measurement phase specifically
    const [imageErrors, setImageErrors] = useState({});  // Track image load errors by card id
    const [compactStatusStyle, setCompactStatusStyle] = useState({
        headerPadPx: 4,
        headerMinWidthPx: 10,
        statusPadPx: 2,
        headerFontRem: 0.75,
        statusLabelRem: 0.8,
        statusValueRem: 1,
        bodyPaddingPx: 6,
        avatarSizePx: 50,
        avatarFontPx: 18,
        ownerFontRem: 0.85,
        topLeftFontRem: 1.0,
        titleFontRem: 1.5,
        descFontRem: 0.9,
        metaFontRem: 0.75,
        titleLines: 1,
        descLines: 2,
        containerPadPx: 2,
        avatarWrapWidthPx: 80,
        showHeaderText: true,
    });

    useLayoutEffect(() => {
        const layoutDepsChanged =
            prevLayoutDepsRef.current.minOpenTicketCount !== minOpenTicketCount ||
            prevLayoutDepsRef.current.isOpenTicketPlacementTop !== isOpenTicketPlacementTop;
        const prevData = prevDisplayCardsRef.current;
        prevDisplayCardsRef.current = displayCardsData;
        prevLayoutDepsRef.current = { minOpenTicketCount, isOpenTicketPlacementTop };

        if (hasInitiallyLoadedRef.current && !layoutDepsChanged && areSameCards(prevData, displayCardsData)) {
            return;
        }

        // Increment data version to invalidate any pending auto-reverts
        dataVersionRef.current += 1;

        initialStateRef.current = null;
        hasUserInteractedRef.current = false;
        isFirstCalculationRef.current = true;
        calcRetryRef.current = 0;
        calcRunIdRef.current += 1;
        cardRefs.current = new Array(displayCardsData.length);
        entryAnimTimersRef.current.forEach(clearTimeout);
        entryAnimTimersRef.current = [];
        setEntryAnimStage(0);

        // Clear any pending auto-revert timer when data changes
        if (autoRevertTimerRef.current) {
            clearTimeout(autoRevertTimerRef.current);
            autoRevertTimerRef.current = null;
        }

        // Reset image errors when data changes
        setImageErrors({});

        if (displayCardsData.length === 0) {
            setCardStates([]);
            setIsReady(true);
            setDisableBodyTransition(false);
            hasInitiallyLoadedRef.current = true;
            return;
        }

        // Deterministic refresh: always reset and recalculate to avoid stale layout/state
        isCalculatingRef.current = true;
        setIsReady(false);
        setDisableBodyTransition(true);
        setDisableHeaderTransition(true);
        setCardStates(
            displayCardsData.map((_, index) => ({
                isExpanded: false,
                isHalfOpen: false,
                fullHeight: 0,
                visibleBodyHeight: 0,
                renderMode: "collapsed",
                priorCompressedMode: "collapsed",
            }))
        );

        const scheduleCalc = (delayMs = 0) => {
            const run = () => {
                const doCalc = () => requestAnimationFrame(() => requestAnimationFrame(() => calcHandlerRef.current?.()));
                if (document.fonts && document.fonts.ready) {
                    document.fonts.ready.then(doCalc).catch(doCalc);
                } else {
                    doCalc();
                }
            };
            if (delayMs > 0) return setTimeout(run, delayMs);
            run();
            return null;
        };

        const timer = scheduleCalc(0);

        return () => {
            if (timer) clearTimeout(timer);
            if (autoRevertTimerRef.current) {
                clearTimeout(autoRevertTimerRef.current);
                autoRevertTimerRef.current = null;
            }
            if (revertAnimationTimerRef.current) {
                clearTimeout(revertAnimationTimerRef.current);
                revertAnimationTimerRef.current = null;
            }
            isRevertAnimatingRef.current = false;
            isEntryAnimatingRef.current = false;
            entryAnimTimersRef.current.forEach(clearTimeout);
            entryAnimTimersRef.current = [];
        };
    }, [displayCardsData, minOpenTicketCount, isOpenTicketPlacementTop]);

    useEffect(() => {
        // Recalculate after images and fonts load to stabilize first render vs refresh
        const triggerCalc = () => {
            const run = () => requestAnimationFrame(() => requestAnimationFrame(() => calcHandlerRef.current?.()));
            if (document.fonts && document.fonts.ready) {
                document.fonts.ready.then(run).catch(run);
            } else {
                run();
            }
        };

        const timer = setTimeout(() => {
            if (!hasInitiallyLoadedRef.current) {
                triggerCalc();
            }
        }, 300);

        const images = parentRef.current
            ? parentRef.current.querySelectorAll(".card img")
            : [];
        const handleImageLoad = () => triggerCalc();

        images.forEach((img) => {
            if (!img.complete) {
                img.addEventListener("load", handleImageLoad);
                img.addEventListener("error", handleImageLoad);
            }
        });

        return () => {
            clearTimeout(timer);
            images.forEach((img) => {
                img.removeEventListener("load", handleImageLoad);
                img.removeEventListener("error", handleImageLoad);
            });
        };
    }, [displayCardsData]);

    useEffect(() => {
        // Simple window resize handler with debounce
        const handleResize = () => {
            if (resizeDebounceRef.current) {
                clearTimeout(resizeDebounceRef.current);
            }
            resizeDebounceRef.current = setTimeout(() => {
                calcHandlerRef.current?.();
                resizeDebounceRef.current = null;
            }, 300);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            if (resizeDebounceRef.current) {
                clearTimeout(resizeDebounceRef.current);
            }
            window.removeEventListener("resize", handleResize);
            // Clean up auto-revert timer on unmount
            if (autoRevertTimerRef.current) {
                clearTimeout(autoRevertTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        const handleWheel = (e) => {
            if (e.deltaY === 0) return;
            e.preventDefault();
            el.scrollLeft += e.deltaY;
        };
        el.addEventListener("wheel", handleWheel, { passive: false });
        return () => el.removeEventListener("wheel", handleWheel);
    }, []);

    useEffect(() => {
        calcHandlerRef.current = calculateAndAdjustHeight;
    });

    useLayoutEffect(() => {
        if (!isReady || displayCardsData.length === 0) return;

        const token = layoutMeasureTokenRef.current;
        if (token === appliedMeasureTokenRef.current) return;

        let raf1 = 0;
        let raf2 = 0;
        let settleTimer = null;

        const measureAndApply = () => {
            const gridEl = gridRef.current;
            const cardElements = cardRefs.current.slice(0, displayCardsData.length).filter(Boolean);
            if (!gridEl || cardElements.length === 0) {
                appliedMeasureTokenRef.current = token;
                return;
            }

            const gridStyles = window.getComputedStyle(gridEl);
            const paddingRight = parseFloat(gridStyles.paddingRight || "0") || 0;

            const gridRect = gridEl.getBoundingClientRect();
            const contentRightX = gridRect.left + gridEl.clientWidth - paddingRight;
            const lastCardRightX = cardElements.reduce((maxRight, el) => {
                const rect = el.getBoundingClientRect();
                return Math.max(maxRight, rect.right);
            }, Number.NEGATIVE_INFINITY);

            const trailingEmptySpace = Math.max(0, Number((contentRightX - lastCardRightX).toFixed(3)));

            appliedMeasureTokenRef.current = token;

            // After user interaction: cards sit at their toggled widths — skip redistribution
            // to avoid fighting the "no other changes" toggle contract.
            if (hasUserInteractedRef.current) return;
            if (isEntryAnimatingRef.current) return;

            if (isRevertAnimatingRef.current) return;

            if (trailingEmptySpace <= 0.5) return;

            setCardStates((prevStates) => {
                const openIndexes = [];
                prevStates.forEach((state, idx) => {
                    if (state.isExpanded) openIndexes.push(idx);
                });

                if (openIndexes.length === 0) return prevStates;

                const openIndexSet = new Set(openIndexes);
                const extraPerOpenCard = trailingEmptySpace / openIndexes.length;

                const nextStates = prevStates.map((state, idx) => {
                    if (!openIndexSet.has(idx)) return state;
                    return {
                        ...state,
                        visibleBodyHeight: Math.max(0, Number(state.visibleBodyHeight || 0)) + extraPerOpenCard,
                    };
                });

                // Keep auto-revert baseline aligned with post-measure width allocation
                // (only reached on initial load since hasUserInteractedRef is false here).
                if (initialStateRef.current &&
                    initialStateRef.current.dataVersion === dataVersionRef.current &&
                    initialStateRef.current.states.length === nextStates.length) {
                    initialStateRef.current = {
                        ...initialStateRef.current,
                        states: JSON.parse(JSON.stringify(nextStates)),
                    };
                }

                return nextStates;
            });
        };

        // CSS keyframe animations are immune to React re-renders — safe to measure
        // at normal settle time without disrupting the running animation.
        const settleDelayMs = disableBodyTransition ? 150 : 260;
        raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => {
                if (settleDelayMs > 0) {
                    settleTimer = setTimeout(measureAndApply, settleDelayMs);
                } else {
                    measureAndApply();
                }
            });
        });

        return () => {
            if (raf1) cancelAnimationFrame(raf1);
            if (raf2) cancelAnimationFrame(raf2);
            if (settleTimer) clearTimeout(settleTimer);
        };
    }, [cardStates, isReady, displayCardsData.length, disableBodyTransition]);

    // One-shot width correction: when a card transitions to open after a user toggle,
    // measure its real scrollWidth and grow visibleBodyHeight (used as flexBasis) if
    // the pre-calculated estimate is too small. Skipped on initial load.
    useLayoutEffect(() => {
        if (!isReady || !hasUserInteractedRef.current) return;
        const currentExpanded = new Set(
            cardStates.flatMap((s, i) => s.isExpanded ? [i] : [])
        );
        const newlyOpened = [...currentExpanded].filter(i => !prevExpandedRef.current.has(i));
        prevExpandedRef.current = currentExpanded;
        if (newlyOpened.length === 0) return;
        const fixes = {};
        newlyOpened.forEach(i => {
            const body = cardRefs.current[i]?.querySelector(".card-body");
            if (!body) return;
            const naturalWidth = body.scrollWidth;
            if (naturalWidth > Number(cardStates[i]?.visibleBodyHeight || 0) + 2) fixes[i] = naturalWidth;
        });
        if (Object.keys(fixes).length === 0) return;
        layoutMeasureTokenRef.current += 1;
        setCardStates(prev => prev.map((s, i) =>
            fixes[i] !== undefined && s.isExpanded
                ? { ...s, visibleBodyHeight: fixes[i], fullHeight: fixes[i] }
                : s
        ));
    }, [cardStates, isReady]);

    const calculateAndAdjustHeight = () => {
        if (displayCardsData.length === 0) {
            setIsReady(true);
            setDisableBodyTransition(false);
            return;
        }

        // Allow first calculation to always run, then throttle subsequent ones
        const now = Date.now();
        if (!isFirstCalculationRef.current && now - lastCalcTimeRef.current < 400) return;

        if (!parentRef.current || cardRefs.current.length === 0) {
            if (calcRetryRef.current < 5) {
                calcRetryRef.current += 1;
                setTimeout(calculateAndAdjustHeight, 100);
            }
            return;
        }

        isFirstCalculationRef.current = false; // Mark first calculation as done
        lastCalcTimeRef.current = now;


        const parentWidth = parentRef.current.clientWidth;
        const cardElements = cardRefs.current.slice(0, displayCardsData.length);

        if (cardElements.length === 0 || cardElements.some((el) => !el)) {
            if (calcRetryRef.current < 5) {
                calcRetryRef.current += 1;
                setTimeout(calculateAndAdjustHeight, 100);
            }
            return;
        }

        isCalculatingRef.current = true;
        isMeasuringRef.current = true;
        setDisableBodyTransition(true);



        // Wait a tick for layout to recalculate
        const currentCalcRunId = ++calcRunIdRef.current;
        requestAnimationFrame(() => {

            const restoreMeasuredBodies = () => {
                // originalHeights.forEach((item) => {
                //     if (item) {
                //         item.body.style.maxHeight = item.original.maxHeight;
                //         item.body.style.display = item.original.display;
                //         item.body.style.overflow = item.original.overflow;
                //         item.body.style.padding = item.original.padding;
                //         item.body.style.margin = item.original.margin;
                //         item.body.style.visibility = item.original.visibility;
                //         item.body.style.position = item.original.position;
                //         item.body.style.left = item.original.left;
                //         item.body.style.right = item.original.right;
                //         item.body.style.width = item.original.width;
                //         item.body.style.pointerEvents = item.original.pointerEvents;
                //     }
                // });
            };

            if (currentCalcRunId !== calcRunIdRef.current) {
                //restoreMeasuredBodies();
                isMeasuringRef.current = false;
                setDisableBodyTransition(false);
                return;
            }

            const cardSpacing = cardElements.map((card) => {
                const styles = window.getComputedStyle(card);
                const marginBottom = parseFloat(styles.gap || "3.5");
                return Number.isFinite(marginBottom) ? marginBottom : 3;
            });

            // Calculate layout - use actual rendered grid height to avoid drift from extra paddings/offsets
            const scrollContainer = parentRef.current.querySelector(".slim-scroll, .slim-scroll-dark");
            //const containerWidth = gridRef.current?.clientHeight || (scrollContainer ? scrollContainer.clientHeight : parentHeight);
            const containerWidth = gridRef.current?.clientWidth || (scrollContainer ? scrollContainer.clientWidth : parentWidth);
            const primaryOpenBodyPx = defaultWidthOfOpenCard;

            // Measure all card body heights
            const cardHeights = cardElements.map((card) => {
                //const body = card.querySelector(".card-body");
                return primaryOpenBodyPx;
                //return body ? body.scrollWidth : 0;
            });

            // Restore original display state
            restoreMeasuredBodies();

            // Measurement phase is complete.
            // Keep disableBodyTransition = true so the initial layout render has no
            // flex-basis animation. This ensures measureAndApply reads settled DOM widths
            // (not mid-animation values) when it fires 150ms later.
            // disableBodyTransition is set to false in toggleCard on first user interaction.
            isMeasuringRef.current = false;



            // Dynamically compress header/status area based on available width per card
            const containerPadPx = 2;
            const availableWidth = containerWidth - (containerPadPx * 2);
            const perCardHeight = availableWidth / Math.max(cardElements.length, 1);
            const compressionRatio = Math.max(0, Math.min(1, (90 - perCardHeight) / 60));
            const roomyRatio = 1 - compressionRatio;

            const headerPadPx = 2;  //Math.max(0, Math.round(4 * roomyRatio));
            const headerMinWidthPx = Math.max(2, Math.round(3 + (11 * roomyRatio)));
            const statusPadPx = Math.max(0, Math.round(4 * roomyRatio));
            const headerFontRem = Number((0.56 + (0.26 * roomyRatio)).toFixed(2));

            // Status text - keep standard sizes for clear readability
            const statusLabelRem = 0.8;
            const statusValueRem = 1;

            // Body content - keep standard sizes for clear readability
            const bodyPaddingPx = 6;
            const avatarSizePx = 50;
            const avatarFontPx = 18;
            const ownerFontRem = 0.85;
            const topLeftFontRem = 1.0;
            const titleFontRem = 1.5;
            const descFontRem = 0.9;
            const metaFontRem = 0.75;
            const titleLines = 2;
            const descLines = 2;

            const avatarWrapWidthPx = 80;
            const showHeaderText = true;

            const newCardStates = displayCardsData.map((_, index) => ({
                isExpanded: false,
                isHalfOpen: false,
                fullHeight: cardHeights[index] || 0,  // Store full height
                visibleBodyHeight: 0,
                renderMode: "collapsed",
                priorCompressedMode: "collapsed",
            }));


            const safeMinOpenCount = Number.isFinite(Number(minOpenTicketCount))
                ? Math.max(1, Math.min(5, Math.floor(Number(minOpenTicketCount))))
                : 1;

            const totalSpacing = cardSpacing.slice(0, -1).reduce((sum, spacing) => sum + spacing, 0);
            setCompactStatusStyle((prev) => {
                if (
                    prev.headerPadPx === headerPadPx &&
                    prev.headerMinWidthPx === headerMinWidthPx &&
                    prev.statusPadPx === statusPadPx &&
                    prev.headerFontRem === headerFontRem &&
                    prev.statusLabelRem === statusLabelRem &&
                    prev.statusValueRem === statusValueRem &&
                    prev.bodyPaddingPx === bodyPaddingPx &&
                    prev.avatarSizePx === avatarSizePx &&
                    prev.avatarFontPx === avatarFontPx &&
                    prev.ownerFontRem === ownerFontRem &&
                    prev.topLeftFontRem === topLeftFontRem &&
                    prev.titleFontRem === titleFontRem &&
                    prev.descFontRem === descFontRem &&
                    prev.metaFontRem === metaFontRem &&
                    prev.titleLines === titleLines &&
                    prev.descLines === descLines &&
                    prev.containerPadPx === containerPadPx &&
                    prev.avatarWrapWidthPx === avatarWrapWidthPx &&
                    prev.showHeaderText === showHeaderText
                ) {
                    return prev;
                }
                return {
                    headerPadPx,
                    headerMinWidthPx: headerMinWidthPx,
                    statusPadPx,
                    headerFontRem,
                    statusLabelRem,
                    statusValueRem,
                    bodyPaddingPx,
                    avatarSizePx,
                    avatarFontPx,
                    ownerFontRem,
                    topLeftFontRem,
                    titleFontRem,
                    descFontRem,
                    metaFontRem,
                    titleLines,
                    descLines,
                    containerPadPx,
                    avatarWrapWidthPx,
                    showHeaderText,
                };
            });

            const normalHeaderPadPxForCalc = 0; //Math.max(2, headerPadPx + 1);
            const normalHeaderMinWidthPxForCalc = defaultOpenHeaderBlockPx; // Math.max(12, headerMinWidthPx + 2);
            const mediumHeaderPadPxForCalc = normalHeaderPadPxForCalc; //Math.max(1, normalHeaderPadPxForCalc - 1);
            const mediumHeaderMinWidthPxForCalc = normalHeaderMinWidthPxForCalc; //Math.max(8, normalHeaderMinWidthPxForCalc - 3);
            const collapsedHeaderPadPxForCalc = 0;// Math.max(0, mediumHeaderPadPxForCalc - 1);
            const collapsedHeaderMinWidthPxForCalc = 4; // Math.max(2, mediumHeaderMinWidthPxForCalc - 5);

            const normalHeaderBlockPxForCalc = normalHeaderMinWidthPxForCalc + (normalHeaderPadPxForCalc * 2);
            const mediumHeaderBlockPxForCalc = mediumHeaderMinWidthPxForCalc + (mediumHeaderPadPxForCalc * 2);
            const collapsedHeaderBlockPxForCalc = collapsedHeaderMinWidthPxForCalc + (collapsedHeaderPadPxForCalc * 2);

            // Baseline all cards as highly-compressed first, then upgrade selected cards to medium/open.
            const compressedHeaderWidth = collapsedHeaderBlockPxForCalc;
            const collapsedBaselineWidth = cardElements.length * compressedHeaderWidth + totalSpacing;
            let availableBodyWidth = Math.max(0, availableWidth - collapsedBaselineWidth);

            // ─── Reserve space for guaranteed medium (orphan) header upgrades ────
            // Each medium card costs (mediumHeaderBlockPx - collapsedHeaderBlockPx)
            // more than the collapsed baseline.  Subtract this upfront so the
            // open-card fit check doesn't claim space that belongs to orphan headers.
            //
            // Priority: open cards first, then orphan headers, then stack headers.
            const mediumUpgradePerCardPx = Math.max(0, mediumHeaderBlockPxForCalc - collapsedHeaderBlockPxForCalc);
            const potentialMediumCount = Math.min(Math.max(0, cardElements.length - 1), MIN_MEDIUM_HEADERS);
            const reservedMediumUpgradePx = potentialMediumCount * mediumUpgradePerCardPx;
            const availableBodyForOpenFit = Math.max(0, availableBodyWidth - reservedMediumUpgradePx);

            const fullOpenFallbackPx = defaultWidthOfOpenCard;

            const buildOpenIndexes = (count) => (count > 0
                ? (isOpenTicketPlacementTop
                    ? Array.from({ length: count }, (_, idx) => idx)
                    : Array.from({ length: count }, (_, idx) => cardElements.length - count + idx))
                : []);

            const allOpenHeight = cardElements.reduce((sum, _, idx) => {
                const fullHeight = Math.max(1, Math.floor(cardHeights[idx] || fullOpenFallbackPx));
                return sum + fullHeight + normalHeaderBlockPxForCalc;
            }, 0) + totalSpacing;
            const allCardsFitOpen = allOpenHeight <= availableWidth;

            const targetOpenCount = Math.min(safeMinOpenCount, cardElements.length);
            let fittedOpenCount = allCardsFitOpen ? cardElements.length : targetOpenCount;
            if (!allCardsFitOpen) {
                while (fittedOpenCount > 0) {
                    const candidateIndexes = buildOpenIndexes(fittedOpenCount);
                    const candidateOpenHeights = candidateIndexes.reduce((sum, idx) => {
                        const fullHeight = Math.max(1, Math.floor(cardHeights[idx] || fullOpenFallbackPx));
                        return sum + fullHeight;
                    }, 0);
                    const candidateHeaderCost = candidateIndexes.length * normalHeaderBlockPxForCalc;
                    // Use medium-reserve-adjusted budget so orphan headers are
                    // guaranteed to fit alongside however many cards are open.
                    if ((candidateOpenHeights + candidateHeaderCost) <= availableBodyForOpenFit) {
                        break;
                    }
                    fittedOpenCount -= 1;
                }
            }

            const openIndexes = buildOpenIndexes(fittedOpenCount);
            const openIndexSet = new Set(openIndexes);
            const openCardsMeta = openIndexes.map((idx) => ({
                idx,
                fullHeight: Math.max(1, Math.floor(cardHeights[idx] || fullOpenFallbackPx)),
            }));

            const applyOpenTicketLayout = (openBodyBudgetPx) => {
                const safeBudget = Math.max(0, Math.floor(openBodyBudgetPx));
                const openCount = openCardsMeta.length;
                if (openCount === 0 || safeBudget <= 0) return 0;

                const allocatedBodies = openCardsMeta.map((item) => item.fullHeight);
                const requiredBudget = allocatedBodies.reduce((sum, value) => sum + value, 0);
                if (safeBudget < requiredBudget) return 0;

                openCardsMeta.forEach((item, orderIdx) => {
                    const visibleBodyHeight = Math.max(1, Math.floor(allocatedBodies[orderIdx]));
                    newCardStates[item.idx].isExpanded = true;
                    newCardStates[item.idx].isHalfOpen = false;
                    newCardStates[item.idx].visibleBodyHeight = visibleBodyHeight;
                    newCardStates[item.idx].renderMode = "open";
                });

                return Math.min(
                    safeBudget,
                    openCardsMeta.reduce((sum, _, idx) => sum + Math.max(1, Math.floor(allocatedBodies[idx])), 0)
                );
            };

            if (openIndexes.length > 0) {
                // Feed the adjusted budget so open cards don't consume orphan header space.
                const openHeaderBudgetPx = openIndexes.length * normalHeaderBlockPxForCalc;
                const openBodyBudgetPx = Math.max(0, availableBodyForOpenFit - openHeaderBudgetPx);

                const consumedOpenBodyPx = applyOpenTicketLayout(openBodyBudgetPx);
                const consumedOpenBudgetPx = openHeaderBudgetPx + consumedOpenBodyPx;
                // Update the full availableBodyWidth (not the adjusted one) so the
                // medium reserve remains in the budget for applyCompressedModes.
                availableBodyWidth = Math.max(0, availableBodyWidth - consumedOpenBudgetPx);
            }

            const remainingIndexes = [];
            for (let idx = 0; idx < cardElements.length; idx++) {
                if (!openIndexSet.has(idx)) {
                    remainingIndexes.push(idx);
                }
            }



            const applyCompressedModes = (indexes, remainingBodyHeightPx) => {
                const allocationOrder = isOpenTicketPlacementTop
                    ? indexes
                    : [...indexes].reverse();
                const nonOpenCount = indexes.length;
                const dynamicSpaceForCompressedModes = Math.max(0, remainingBodyHeightPx);
                //const mediumUpgradePx = Math.max(1, mediumHeaderBlockPxForCalc - collapsedHeaderBlockPxForCalc);
                const mediumUpgradePx = Math.max(1, mediumHeaderBlockPxForCalc);
                const maxMediumByBudget = Math.min(nonOpenCount, Math.floor(dynamicSpaceForCompressedModes / mediumUpgradePx));

                // Guarantee MIN_MEDIUM_HEADERS orphan headers only when remaining
                // space can actually afford them.  If not (e.g. open cards consumed
                // most of the viewport) fall back to budget-based count to avoid overflow.
                const canAffordGuarantee = dynamicSpaceForCompressedModes >= (MIN_MEDIUM_HEADERS * mediumUpgradePx);
                const guaranteedMedium = canAffordGuarantee ? Math.min(nonOpenCount, MIN_MEDIUM_HEADERS) : 0;
                const mediumCount = Math.max(guaranteedMedium, maxMediumByBudget);
                const consumedBudgetPx = mediumCount * mediumUpgradePx;

                //let findFirstLastMedium = false;

                allocationOrder.forEach((idx, orderIdx) => {
                    const shouldBeMedium = orderIdx < mediumCount;

                    // newCardStates[idx].lastCollapsedMode = false;
                    // if (!shouldBeMedium && !findFirstLastMedium) {
                    //     findFirstLastMedium = true;
                    //     console.log("First medium card found at index", idx);
                    //     newCardStates[idx].lastCollapsedMode = true;
                    // }

                    newCardStates[idx].isExpanded = false;
                    newCardStates[idx].isHalfOpen = false;
                    newCardStates[idx].visibleBodyHeight = 0;
                    newCardStates[idx].renderMode = shouldBeMedium ? "medium" : "collapsed";
                    newCardStates[idx].priorCompressedMode = shouldBeMedium ? "medium" : "collapsed";
                });

                return Math.max(0, dynamicSpaceForCompressedModes - consumedBudgetPx);
            };

            applyCompressedModes(remainingIndexes, availableBodyWidth);

            // Mark calculation as complete BEFORE setting states
            isCalculatingRef.current = false;
            // (disableBodyTransition is already set to false after measurement phase)

            if (currentCalcRunId !== calcRunIdRef.current) return;

            layoutMeasureTokenRef.current += 1;

            // Store the initial calculated state for auto-revert with data version
            if (!initialStateRef.current) {
                initialStateRef.current = {
                    states: JSON.parse(JSON.stringify(newCardStates)),  // Deep copy
                    dataVersion: dataVersionRef.current
                };
            }

            // ── Entry animation: 3-stage sequence ──────────────────────────────
            // Stage 1: cards appear suddenly (no fade/slide), one by one left→right
            // Stage 2: headers expand one by one left→right
            // Stage 3: bodies expand one by one left→right
            const stage1States = newCardStates.map(s => ({
                ...s,
                isExpanded: false,
                visibleBodyHeight: 0,
                renderMode: "collapsed",
                lastCollapsedMode: false,
            }));

            hasInitiallyLoadedRef.current = true;
            setCardStates(stage1States);
            calcRetryRef.current = 0;

            requestAnimationFrame(() => {
                if (currentCalcRunId !== calcRunIdRef.current) return;
                isEntryAnimatingRef.current = true;

                const N = displayCardsData.length;
                const STAGGER_CARD = 60;
                const STAGGER_HEADER = 250;  // matches entry header transition (220ms) + buffer
                const STAGGER_BODY = 950;    // matches body transition (900ms) + buffer
                const HEADER_TRANSITION_MS = 220; // entry transition duration (must match CSS above)
                const BODY_TRANSITION_MS = 900;   // same as interactive body transition

                const addTimer = (fn, delay) => {
                    const id = setTimeout(() => {
                        if (currentCalcRunId !== calcRunIdRef.current) return;
                        fn();
                    }, delay);
                    entryAnimTimersRef.current.push(id);
                };

                setEntryAnimStage(1);
                setIsReady(true);

                const startStage3 = () => {
                    setDisableBodyTransition(false);
                    setEntryAnimStage(3);
                    const revealBody = (i) => {
                        if (i >= N) {
                            addTimer(() => {
                                setEntryAnimStage(0);
                                isEntryAnimatingRef.current = false;
                                layoutMeasureTokenRef.current += 1;
                                setCardStates(prev => prev.map(s => ({ ...s })));
                            }, BODY_TRANSITION_MS + 200);
                            return;
                        }
                        if (newCardStates[i].isExpanded) {
                            setCardStates(prev => {
                                const next = prev.map(s => ({ ...s }));
                                next[i] = {
                                    ...next[i],
                                    isExpanded: true,
                                    renderMode: "open",
                                    visibleBodyHeight: newCardStates[i].visibleBodyHeight,
                                };
                                return next;
                            });
                            addTimer(() => revealBody(i + 1), STAGGER_BODY);
                        } else {
                            revealBody(i + 1); // no visual change — skip immediately
                        }
                    };
                    requestAnimationFrame(() => {
                        if (currentCalcRunId !== calcRunIdRef.current) return;
                        revealBody(0);
                    });
                };

                const startStage2 = () => {
                    setDisableHeaderTransition(false);
                    setEntryAnimStage(2);
                    const revealHeader = (i) => {
                        if (i >= N) {
                            addTimer(startStage3, HEADER_TRANSITION_MS + 80);
                            return;
                        }
                        const state = newCardStates[i];
                        const targetMode = state.renderMode === "open" ? "medium" : state.renderMode;
                        if (targetMode !== "collapsed") {
                            setCardStates(prev => {
                                const next = prev.map(s => ({ ...s }));
                                next[i] = {
                                    ...next[i],
                                    renderMode: targetMode,
                                    priorCompressedMode: state.priorCompressedMode,
                                    lastCollapsedMode: state.lastCollapsedMode,
                                };
                                return next;
                            });
                            addTimer(() => revealHeader(i + 1), STAGGER_HEADER);
                        } else {
                            revealHeader(i + 1); // no visual change — skip immediately
                        }
                    };
                    requestAnimationFrame(() => {
                        if (currentCalcRunId !== calcRunIdRef.current) return;
                        revealHeader(0);
                    });
                };

                const lastCardTime = Math.min(N - 1, 8) * STAGGER_CARD;
                addTimer(startStage2, lastCardTime + 80);
            });
        });
    };

    const toggleCard = (index) => {
        hasUserInteractedRef.current = true;
        setScrollEnabled(true);
        setDisableBodyTransition(false);
        setDisableHeaderTransition(false);
        if (revertAnimationTimerRef.current) {
            clearTimeout(revertAnimationTimerRef.current);
            revertAnimationTimerRef.current = null;
        }
        isRevertAnimatingRef.current = false;
        layoutMeasureTokenRef.current += 1;

        setCardStates((prevStates) => {
            const newStates = prevStates.map(s => ({ ...s }));
            const currentMode = newStates[index]?.renderMode;

            if (currentMode === "open") {
                // Open ticket header clicked → collapse to orphan header only
                newStates[index].isExpanded = false;
                newStates[index].isHalfOpen = false;
                newStates[index].visibleBodyHeight = 0;
                newStates[index].renderMode = "medium";
                newStates[index].priorCompressedMode = "medium";
            } else if (currentMode === "medium") {
                // Orphan header clicked → open this ticket only.
                // Match the width of any already-open card so all open cards are the same size.
                const alreadyOpen = newStates.find((s, i) => i !== index && s.isExpanded);
                const consistentWidth = alreadyOpen
                    ? alreadyOpen.visibleBodyHeight
                    : (newStates[index].fullHeight || defaultWidthOfOpenCard);
                newStates[index].isExpanded = true;
                newStates[index].isHalfOpen = false;
                newStates[index].visibleBodyHeight = consistentWidth;
                newStates[index].renderMode = "open";
            } else {
                // Stack (collapsed) clicked → convert clicked + before + after to orphan headers
                const toConvert = new Set([index]);
                if (index > 0) toConvert.add(index - 1);
                if (index < newStates.length - 1) toConvert.add(index + 1);

                toConvert.forEach((i) => {
                    newStates[i].isExpanded = false;
                    newStates[i].isHalfOpen = false;
                    newStates[i].visibleBodyHeight = 0;
                    newStates[i].renderMode = "medium";
                    newStates[i].priorCompressedMode = "medium";
                });
            }

            return newStates;
        });

        if (autoRevertTimerRef.current) {
            clearTimeout(autoRevertTimerRef.current);
        }

        if (autoRevertDurationinSec > 0) {
            const currentVersion = dataVersionRef.current;
            autoRevertTimerRef.current = setTimeout(() => {
                if (initialStateRef.current &&
                    initialStateRef.current.dataVersion === currentVersion &&
                    initialStateRef.current.states.length === cardStates.length) {
                    const restoredState = JSON.parse(JSON.stringify(initialStateRef.current.states));
                    // Reset interaction flags so measureAndApply can re-run after the
                    // revert animation settles. Allow transitions so the reset animates smoothly.
                    hasUserInteractedRef.current = false;
                    prevExpandedRef.current = new Set();
                    setScrollEnabled(false);
                    isRevertAnimatingRef.current = true;
                    setDisableBodyTransition(false);
                    setDisableHeaderTransition(false);
                    layoutMeasureTokenRef.current += 1;
                    setCardStates(restoredState);
                    revertAnimationTimerRef.current = setTimeout(() => {
                        isRevertAnimatingRef.current = false;
                        revertAnimationTimerRef.current = null;
                        setDisableBodyTransition(true);
                    }, 1100);
                }
                autoRevertTimerRef.current = null;
            }, autoRevertDurationinSec * 1000);
        }
    };

    const formatOwnerName = (name) => {
        if (!name) return "";
        const words = name.trim().split(/\s+/);
        if (words.length === 1) return words[0];

        // First word as is, rest as initials
        const formatted = [words[0]];
        for (let i = 1; i < words.length; i++) {
            formatted.push(words[i][0].toUpperCase());
        }
        return formatted.join(" ");
    };

    const getInitials = (name) => {
        if (!name) return "?";
        const words = name.trim().split(/\s+/);
        if (words.length === 1) return words[0][0]?.toUpperCase() || "?";
        // First letter of first word + first letter of last word
        return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    };

    const handleImageError = (cardId) => {
        setImageErrors((prev) => ({ ...prev, [cardId]: true }));
    };

    const shouldShowCards = isReady || hasInitiallyLoadedRef.current;

    return (
        <div ref={parentRef} className={classes.landscapeRoot} style={{ height: "100%", width: "100%" }}>
            <div
                ref={scrollContainerRef}
                className={`${isDarkTheme ? "slim-scroll-dark" : "slim-scroll"} ${classes.landscapeScroll}`}
                style={{
                    width: "100%",
                    height: "100%",
                    overflowX: scrollEnabled ? "auto" : "hidden",
                    overflowY: "hidden",
                    opacity: shouldShowCards ? 1 : 0,
                    visibility: shouldShowCards ? "visible" : "hidden",
                    transition: "none",
                    backgroundColor: isDarkTheme ? "#1a1a1a" : "#f0f0f0",
                }}
            >
                <div
                    className={classes.landscapeGrid}
                    ref={gridRef}
                    style={{
                        // paddingTop: "8px",
                        // paddingLeft: "8px",
                        // paddingRight: "8px",
                        // paddingBottom: "8px",
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "nowrap",
                        alignItems: "stretch",
                        alignContent: "flex-start",
                        gap: "0px",
                        height: "100%",
                        boxSizing: "border-box",
                        margin: "0 8px",
                    }}
                >
                    {displayCardsData.map((card, index) => (
                        (() => {
                            //console.log("Rendering card index:", index, "with state:", cardStates[index]);
                            const cardState = cardStates[index] || {};
                            const isExpanded = Boolean(cardState.isExpanded);
                            const isHalfOpen = Boolean(cardState.isHalfOpen);
                            const renderMode = (cardState.renderMode === "open" || isExpanded || isHalfOpen)
                                ? "open"
                                : (cardState.renderMode === "medium" ? "medium" : "collapsed");
                            const isCollapsedMode = renderMode === "collapsed";
                            const isMediumMode = renderMode === "medium";
                            const isBodyOpen = renderMode === "open";
                            const cardStaggerDelay = Math.min(index, 8) * 55;
                            const bodyAnimation = "none";
                            const fullWidth = Number(cardState.fullHeight || 0);
                            const visibleBodyWidth = Number(cardState.visibleBodyHeight || 0);
                            const bodyWidth = renderMode === "open"
                                ? `${Math.max(0, Math.max(1, visibleBodyWidth || fullWidth)).toFixed(3)}px`
                                : "0px";
                            const bodyDisplay = "flex";
                            const bodyPadding = renderMode === "open" ? `${compactStatusStyle.bodyPaddingPx}px` : "0";
                            const bodyMargin = "0";
                            const contentOpacity = isBodyOpen ? 1 : 0;
                            const contentTransitionDisabled = ((entryAnimStage === 1 || entryAnimStage === 2) && !hasUserInteractedRef.current) || disableBodyTransition;
                            const contentTransition = (delayMs) => contentTransitionDisabled ? "none" : `opacity 680ms cubic-bezier(0.22, 1, 0.36, 1) ${delayMs}ms`;
                            const normalHeaderPadPx = 0;
                            const normalHeaderMinWidthPx = 26; // matches defaultOpenHeaderBlockPx in calc
                            const normalHeaderFontRem = 0.9;

                            const mediumHeaderPadPx = normalHeaderPadPx;
                            const mediumHeaderMinWidthPx = normalHeaderMinWidthPx;
                            const mediumHeaderFontRem = normalHeaderFontRem;

                            const collapsedHeaderPadPx = 0; // Math.max(0, mediumHeaderPadPx - 1);
                            const collapsedHeaderMinWidthPx = 3; // Math.max(4, mediumHeaderMinWidthPx - 5);
                            const collapsedHeaderFontRem = Math.max(0.44, Number((mediumHeaderFontRem - 0.18).toFixed(2)));

                            const headerPadPx = isCollapsedMode
                                ? collapsedHeaderPadPx
                                : isMediumMode
                                    ? mediumHeaderPadPx
                                    : normalHeaderPadPx;
                            const headerFontSize = isCollapsedMode
                                ? `${collapsedHeaderFontRem}rem`
                                : isMediumMode
                                    ? `${mediumHeaderFontRem}rem`
                                    : `${normalHeaderFontRem}rem`;
                            const headerLineHeight = isCollapsedMode ? 0.5 : isMediumMode ? 0.7 : 1;

                            const headerDisplayText = `${isCollapsedMode ? "" : (card.headerText || "")}`;
                            const headerColor = getHeaderColor(card.percent);
                            const percentStr = card.percent || "";
                            //const headerTextWidthPx = Math.max(8, Math.ceil(parseFloat(headerFontSize) * 16));
                            const headerWidthPx = isCollapsedMode
                                ? collapsedHeaderMinWidthPx
                                : isMediumMode
                                    ? mediumHeaderMinWidthPx
                                    : normalHeaderMinWidthPx;


                            return (
                                <div
                                    className={`card ${classes.landscapeCard}`}
                                    key={card.id}
                                    ref={(el) => (cardRefs.current[index] = el)}
                                    style={{
                                        backgroundColor: themeStyles.cardBackground,
                                        border: isCollapsedMode ? "none" : themeStyles.cardBorder,
                                        boxShadow: isCollapsedMode ? "none" : themeStyles.cardBoxShadow,
                                        color: themeStyles.bodyTextColor,
                                        borderRadius: isCollapsedMode || isMediumMode ? "0" : "5px",
                                        opacity: isReady ? undefined : 0,
                                        animation: (entryAnimStage === 1 && isReady && !hasUserInteractedRef.current)
                                            ? `widgetCardAppear 1ms step-end ${cardStaggerDelay}ms both`
                                            : "none",
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "stretch",
                                        flex: "0 0 auto",
                                        maxWidth: "none",
                                        minWidth: "0px",
                                        height: "calc(100% - 16px)",
                                        minHeight: 0,
                                        boxSizing: "border-box",
                                        overflow: "hidden",
                                        marginTop: isCollapsedMode ? "9px" : "8px",
                                        marginBottom: "8px",
                                        // Minimal gap between collapsed cards and the open/medium block, based on current render state
                                        marginRight: (() => {
                                            const cardGapPx = isDarkTheme ? "5px" : "3px";
                                            return isCollapsedMode
                                                ? (cardState.lastCollapsedMode ? (isOpenTicketPlacementTop ? (cardState.toggledEnabled ? cardGapPx : "0px") : cardGapPx) : "0px")
                                                : (index === displayCardsData.length - 1 ? "0" : cardGapPx);
                                        })(),
                                    }}
                                    onMouseEnter={(event) => {
                                        if (!isReady || isEntryAnimatingRef.current) return;
                                        event.currentTarget.style.animation = "none";
                                        event.currentTarget.style.transition = "transform 420ms cubic-bezier(0.34, 1.56, 0.64, 1)";
                                        event.currentTarget.style.transform = "translateY(-3px)";
                                    }}
                                    onMouseLeave={(event) => {
                                        if (!isReady || isEntryAnimatingRef.current) return;
                                        event.currentTarget.style.transition = "transform 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94)";
                                        event.currentTarget.style.transform = "translateY(0px)";
                                    }}
                                >
                                    <div
                                        className={`card-header fw-bold ${classes.landscapeHeader}`}
                                        style={{
                                            cursor: "pointer",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            paddingTop: isCollapsedMode ? "0px" : "6px",
                                            paddingBottom: isCollapsedMode ? "0px" : "6px",
                                            paddingRight: `${headerPadPx}px`,
                                            paddingLeft: `${headerPadPx}px`,
                                            minHeight: 0,
                                            boxSizing: "border-box",
                                            fontSize: headerFontSize,
                                            lineHeight: headerLineHeight,
                                            backgroundColor: headerColor,
                                            border: isCollapsedMode ? `1px solid ${getStackHeaderBorderColor(headerColor)}` : `1px solid ${headerColor}`,
                                            color: compactStatusStyle.showHeaderText ? "#ffffff" : "transparent",
                                            WebkitFontSmoothing: "antialiased",
                                            MozOsxFontSmoothing: "auto",
                                            textRendering: "geometricPrecision",
                                            letterSpacing: "0.02em",
                                            borderTopLeftRadius: isCollapsedMode || isMediumMode ? "0" : "4px",
                                            borderTopRightRadius: isCollapsedMode || isMediumMode ? "0" : "4px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            alignSelf: "stretch",
                                            textAlign: "center",

                                            transition: disableHeaderTransition
                                                ? "color 250ms ease"
                                                : (() => {
                                                    const hMs = (entryAnimStage === 2 && !hasUserInteractedRef.current) ? 220 : 420;
                                                    return `color 250ms ease, flex-basis ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), max-width ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), min-width ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), font-size ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), padding ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), line-height ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), border-radius ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
                                                })(),
                                            flex: `0 0 ${headerWidthPx}px`,
                                            maxWidth: `${headerWidthPx}px`,
                                            minWidth: `${headerWidthPx}px`,
                                            height: "100%",
                                            maxHeight: "100%",
                                            wordBreak: "normal",
                                            writingMode: "vertical-rl",
                                            textOrientation: "mixed",
                                            transform: "rotate(180deg)",
                                        }}
                                        title={headerDisplayText}
                                        onClick={() => toggleCard(index)}
                                    >
                                        {!isCollapsedMode && (
                                            <>
                                                <span style={{ flex: "0 0 auto", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxHeight: "60%" }}>
                                                    &nbsp;&nbsp;&nbsp;&nbsp;{headerDisplayText}
                                                </span>
                                                {percentStr && (
                                                    <span style={{ flex: "0 0 auto", whiteSpace: "nowrap" }}>
                                                        {percentStr + " of Target"}&nbsp;
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    <div
                                        className={`card-body collapse show ${classes.landscapeBody}`}
                                        style={{
                                            backgroundColor: themeStyles.cardBackground,
                                            color: themeStyles.bodyTextColor,
                                            overflow: "hidden",
                                            animation: bodyAnimation,
                                            transition: disableBodyTransition ? "none" : "flex-basis 900ms cubic-bezier(0.34, 1.08, 0.64, 1), opacity 700ms cubic-bezier(0.22, 1, 0.36, 1)",
                                            maxHeight: "100%",
                                            flexBasis: bodyWidth,
                                            minWidth: 0,
                                            display: bodyDisplay,
                                            padding: bodyPadding,
                                            margin: bodyMargin,
                                            opacity: isBodyOpen ? 1 : 0,
                                            pointerEvents: (isBodyOpen && entryAnimStage === 0) ? "auto" : "none",
                                            borderRadius: "0",
                                            flexGrow: 0,
                                            flexShrink: 0,
                                            alignSelf: "stretch",
                                            boxSizing: "border-box",
                                            contain: "layout paint",
                                            transform: "translateZ(0)",
                                            backfaceVisibility: "hidden",
                                            willChange: !disableBodyTransition ? "flex-basis, opacity" : "auto",
                                        }}
                                    >
                                        <div className={`${classes.landscapeBodySection} ${classes.landscapeBodySectionTop}`}>
                                            <div
                                                className="row align-items-start"
                                                style={{
                                                    display: "flex",
                                                    flexWrap: "nowrap",
                                                    marginLeft: 0,
                                                    marginRight: 0,
                                                    width: "100%",
                                                    opacity: contentOpacity,
                                                    transition: contentTransition(200),
                                                }}
                                            >
                                                <div className="col text-start" style={{ flex: "1 1 0", minWidth: 0, maxWidth: "none", paddingLeft: 0, paddingRight: 0 }}>
                                                    <div style={{ fontSize: `${compactStatusStyle.topLeftFontRem}rem`, fontWeight: "bold" }}>
                                                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                            Target hours: {card.targetHours != null && !isNaN(Number(card.targetHours)) ? +Number(card.targetHours).toFixed(2) : "NA"}
                                                        </div>
                                                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                            Actual hours: {card.actualHours != null && !isNaN(Number(card.actualHours)) ? +Number(card.actualHours).toFixed(2) : "NA"}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col d-flex flex-column align-items-end" style={{ flex: "1 1 0", minWidth: 0, maxWidth: "none", paddingLeft: 0, paddingRight: 0 }}>
                                                    <div style={{ width: `${compactStatusStyle.avatarWrapWidthPx}px`, marginLeft: "auto", display: "flex", justifyContent: "center" }}>
                                                        {!card.imageUrl || imageErrors[card.id] ? (
                                                            <svg
                                                                width={compactStatusStyle.avatarSizePx}
                                                                height={compactStatusStyle.avatarSizePx}
                                                                viewBox="0 0 200 200"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                style={{ borderRadius: "50%" }}
                                                            >
                                                                <circle cx="100" cy="100" r="78" fill="#EBCDB2" stroke="#E3B187" strokeWidth="1.5" />
                                                                <circle cx="100" cy="68" r="26" fill="#B8B8B8" />
                                                                <path d="M72 100 L128 100 L100 178 Z" fill="#B8B8B8" />
                                                            </svg>
                                                        ) : (
                                                            <img
                                                                src={card.imageUrl}
                                                                alt="avatar"
                                                                style={{
                                                                    width: compactStatusStyle.avatarSizePx,
                                                                    height: compactStatusStyle.avatarSizePx,
                                                                    borderRadius: "50%",
                                                                }}
                                                                onError={() => handleImageError(card.id)}
                                                            />
                                                        )}
                                                    </div>

                                                    <div
                                                        style={{
                                                            width: `${compactStatusStyle.avatarWrapWidthPx}px`,
                                                            marginLeft: "auto",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                            textAlign: "center",
                                                            color: themeStyles.bodyTextColor,
                                                            fontSize: `${compactStatusStyle.ownerFontRem}rem`,
                                                        }}
                                                        title={card.owner}
                                                    >
                                                        {formatOwnerName(card.owner)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className={`${classes.landscapeBodySection} ${classes.landscapeBodySectionMiddle}`}>
                                            <div className={classes.landscapeBodyStack}>
                                                <p
                                                    className="fw-bold text-center"
                                                    style={{
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: compactStatusStyle.titleLines,
                                                        WebkitBoxOrient: "vertical",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        fontSize: `${compactStatusStyle.titleFontRem}rem`,
                                                        lineHeight: 1.2,
                                                        marginBottom: "0.15rem",
                                                        opacity: contentOpacity,
                                                        transition: contentTransition(360),
                                                    }}
                                                    title={card.title}
                                                >
                                                    {card.title}
                                                </p>
                                                <p
                                                    className="mt-1 text-center"
                                                    style={{
                                                        color: themeStyles.mutedTextColor,
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: compactStatusStyle.descLines,
                                                        WebkitBoxOrient: "vertical",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        fontSize: `${compactStatusStyle.descFontRem}rem`,
                                                        lineHeight: 1.2,
                                                        marginBottom: "0.4rem",
                                                        opacity: contentOpacity,
                                                        transition: contentTransition(520),
                                                    }}
                                                    title={card.description}
                                                >
                                                    {card.description}
                                                </p>
                                                {card.description1 && (
                                                    <p
                                                        className="text-center"
                                                        style={{
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                            color: themeStyles.mutedTextColor,
                                                            fontSize: `${compactStatusStyle.descFontRem}rem`,
                                                            lineHeight: 1.2,
                                                            marginTop: "0",
                                                            marginBottom: "0.15rem",
                                                            opacity: contentOpacity,
                                                            transition: contentTransition(440),
                                                        }}
                                                        title={card.description1}
                                                    >
                                                        {card.description1}
                                                    </p>
                                                )}

                                            </div>
                                        </div>

                                        <div className={`${classes.landscapeBodySection} ${classes.landscapeBodySectionBottom}`}>
                                            <div
                                                className="row small"
                                                style={{
                                                    display: "flex",
                                                    flexWrap: "nowrap",
                                                    marginLeft: 0,
                                                    marginRight: 0,
                                                    width: "100%",
                                                    fontSize: `${compactStatusStyle.metaFontRem}rem`,
                                                    lineHeight: 1.2,
                                                    paddingBottom: `${compactStatusStyle.bodyPaddingPx + 7}px`,
                                                    opacity: contentOpacity,
                                                    transition: contentTransition(680),
                                                }}
                                            >
                                                <div className="col text-start" style={{ flex: "1 1 0", minWidth: 0, maxWidth: "none", paddingLeft: 0, paddingRight: 0 }}>
                                                    <b>Opened</b>
                                                    <br />
                                                    {card.openDate ?? "NA"}
                                                </div>
                                                <div className="col text-center" style={{ flex: "1 1 0", minWidth: 0, maxWidth: "none", paddingLeft: 0, paddingRight: 0 }}>
                                                    <b>Promised</b>
                                                    <br />
                                                    {card.promiseDate ?? "NA"}
                                                </div>
                                                <div className="col text-end" style={{ flex: "1 1 0", minWidth: 0, maxWidth: "none", paddingLeft: 0, paddingRight: 0 }}>
                                                    <b>Completed</b>
                                                    <br />
                                                    {card.completedDate ?? card.status ?? "NA"}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()
                    ))}
                </div>
            </div>
        </div>
    );
}
